/**
 * `/app/docs` and `/app/docs/:slug` — the standalone documentation site.
 *
 * The Help Center overlay shows the same {@link DocsViewer}; this route exists
 * so a doc can be linked to, bookmarked and refreshed. All view state is in the
 * URL: the slug is a path segment, the section is the hash
 * (`/app/docs/play-entry#conflict-resolution`), and search + the all-roles
 * toggle are query parameters.
 *
 * The two panes are resizable by pointer or keyboard and the width is persisted
 * per device; below the tablet breakpoint the layout stacks and the separator
 * is hidden.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useSession } from '../../app/session-context';
import { STORAGE_KEYS } from '../../lib/constants';
import type { Persona } from '../../lib/personas';
import { readPreference, writePreference } from '../../lib/session-storage';

import { DocsViewer } from './docs-viewer';

const MIN_TOC_WIDTH = 200;
const MAX_TOC_WIDTH = 460;
const DEFAULT_TOC_WIDTH = 280;
const KEYBOARD_STEP = 24;

function clampWidth(value: number): number {
  return Math.min(MAX_TOC_WIDTH, Math.max(MIN_TOC_WIDTH, Math.round(value)));
}

function loadWidth(): number {
  const stored = Number.parseInt(readPreference(STORAGE_KEYS.docsTocWidth) ?? '', 10);
  return Number.isFinite(stored) ? clampWidth(stored) : DEFAULT_TOC_WIDTH;
}

export function DocsPage(): JSX.Element {
  const { slug } = useParams();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { claims, persona: sessionPersona } = useSession();
  // No fallback persona: an unreadable session sees untagged pages only.
  const persona: Persona | null = claims?.persona ?? sessionPersona ?? null;

  const [tocWidth, setTocWidth] = useState(loadWidth);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isDraggingRef = useRef(false);

  const search = searchParams.get('q') ?? '';
  const isAllRoles = searchParams.get('roles') === 'all';
  const sectionId = hash.startsWith('#') ? decodeURIComponent(hash.slice(1)) : null;

  const commitWidth = useCallback((next: number): void => {
    const clamped = clampWidth(next);
    setTocWidth(clamped);
    writePreference(STORAGE_KEYS.docsTocWidth, String(clamped));
  }, []);

  useEffect(() => {
    function onPointerMove(event: PointerEvent): void {
      if (!isDraggingRef.current || containerRef.current === null) return;
      const left = containerRef.current.getBoundingClientRect().left;
      setTocWidth(clampWidth(event.clientX - left));
    }
    function onPointerUp(): void {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      writePreference(STORAGE_KEYS.docsTocWidth, String(tocWidth));
    }
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [tocWidth]);

  const patchParams = useCallback(
    (changes: Record<string, string | null>): void => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === '') next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const onSelect = useCallback(
    (nextSlug: string, nextSection?: string | null): void => {
      const query = searchParams.toString();
      navigate(
        {
          pathname: `/app/docs/${nextSlug}`,
          search: query === '' ? '' : `?${query}`,
          hash: nextSection ? `#${nextSection}` : '',
        },
        { replace: false }
      );
    },
    [navigate, searchParams]
  );

  return (
    <section className="view docs-page">
      <header className="docs-page__header">
        <div>
          <h1 className="view__title">Documentation</h1>
          <p className="muted">
            Product guides bundled with this release. Every page is available offline.
          </p>
        </div>
      </header>

      <div
        className="docs-page__panes"
        ref={containerRef}
        style={{ ['--docs-toc-width' as string]: `${tocWidth}px` }}
      >
        <DocsViewer
          persona={persona}
          slug={slug ?? null}
          sectionId={sectionId}
          search={search}
          isAllRoles={isAllRoles}
          variant="page"
          onSelect={onSelect}
          onSearchChange={(term) => patchParams({ q: term })}
          onAllRolesChange={(next) => patchParams({ roles: next ? 'all' : null })}
        />
        <div
          className="docs-page__resizer"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize contents pane"
          aria-valuenow={tocWidth}
          aria-valuemin={MIN_TOC_WIDTH}
          aria-valuemax={MAX_TOC_WIDTH}
          tabIndex={0}
          onPointerDown={(event) => {
            isDraggingRef.current = true;
            event.currentTarget.setPointerCapture?.(event.pointerId);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowLeft') commitWidth(tocWidth - KEYBOARD_STEP);
            else if (event.key === 'ArrowRight') commitWidth(tocWidth + KEYBOARD_STEP);
            else if (event.key === 'Home') commitWidth(DEFAULT_TOC_WIDTH);
            else return;
            event.preventDefault();
          }}
        />
      </div>
    </section>
  );
}
